const RSS_URL = "https://www.themamahood.co/blog?format=rss";
// Frontend-only CORS workarounds for local dev / restrictive origins.
// Try direct first, then public read-only mirrors that provide CORS headers.
const RSS_FALLBACK_URLS = [
  RSS_URL,
  // AllOrigins raw proxy (adds CORS headers).
  `https://api.allorigins.win/raw?url=${encodeURIComponent(RSS_URL)}`,
  // corsproxy.io (adds CORS headers).
  `https://corsproxy.io/?${encodeURIComponent(RSS_URL)}`,
];

let cachedArticles = null;
let cachedAtMs = 0;
let inFlightPromise = null;

function stripHtmlToText(html) {
  if (!html || typeof html !== "string") return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body?.textContent || "").replace(/\s+/g, " ").trim();
  } catch (_) {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
}

function getFirstParagraphText(html) {
  if (!html || typeof html !== "string") return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const p = doc.querySelector("p");
    const text = (p?.textContent || doc.body?.textContent || "").replace(/\s+/g, " ").trim();
    return text;
  } catch (_) {
    return stripHtmlToText(html);
  }
}

function extractFirstImageUrl(html) {
  if (!html || typeof html !== "string") return null;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const img = doc.querySelector("img");
    const src = img?.getAttribute("src") || img?.getAttribute("data-src");
    return src || null;
  } catch (_) {
    return null;
  }
}

function sanitizeHtml(html) {
  if (!html || typeof html !== "string") return "";
  const doc = new DOMParser().parseFromString(html, "text/html");

  // Remove dangerous/unsupported elements.
  doc.querySelectorAll("script, iframe, object, embed, link, meta").forEach((n) => n.remove());

  // Strip inline event handlers and javascript: urls.
  doc.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = String(attr.value || "");
      if (name.startsWith("on")) el.removeAttribute(attr.name);
      if ((name === "href" || name === "src") && value.trim().toLowerCase().startsWith("javascript:")) {
        el.removeAttribute(attr.name);
      }
      if (name === "style") {
        // keep it simple: remove inline styles to avoid layout surprises
        el.removeAttribute(attr.name);
      }
    });
  });

  return doc.body?.innerHTML || "";
}

function deriveSlugFromLink(link) {
  if (!link) return null;
  try {
    const u = new URL(link);
    const parts = u.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || null;
  } catch (_) {
    const cleaned = String(link).split("?")[0].split("#")[0];
    const parts = cleaned.split("/").filter(Boolean);
    return parts[parts.length - 1] || null;
  }
}

function textFromNode(node) {
  return node?.textContent ? node.textContent.trim() : "";
}

function getFirstNodeText(parent, selectors) {
  for (const sel of selectors) {
    const n = parent.querySelector(sel);
    const t = textFromNode(n);
    if (t) return t;
  }
  return "";
}

function parseRssXmlToArticles(xmlText) {
  const xml = new DOMParser().parseFromString(xmlText, "text/xml");
  const items = Array.from(xml.querySelectorAll("item"));

  return items
    .map((item) => {
      const title = textFromNode(item.querySelector("title")) || "Article";
      const link = textFromNode(item.querySelector("link"));
      const slug = deriveSlugFromLink(link) || title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      const publishedAt = textFromNode(item.querySelector("pubDate"));

      const creator = getFirstNodeText(item, ["dc\\:creator", "creator", "author"]);
      const categories = Array.from(item.querySelectorAll("category"))
        .map((c) => textFromNode(c))
        .filter(Boolean);
      const category = categories[0] || null;

      const contentHtml = getFirstNodeText(item, ["content\\:encoded", "encoded"]);
      const descriptionHtml = getFirstNodeText(item, ["description"]);
      const bodyHtmlRaw = contentHtml || descriptionHtml || "";
      const bodyHtml = sanitizeHtml(bodyHtmlRaw);

      const excerpt =
        getFirstParagraphText(descriptionHtml || bodyHtmlRaw) ||
        stripHtmlToText(descriptionHtml || bodyHtmlRaw).slice(0, 240);

      const heroImage = extractFirstImageUrl(bodyHtmlRaw);

      return {
        slug,
        title,
        excerpt,
        bodyHtml,
        link,
        publishedAt,
        heroImage,
        author: creator || null,
        category,
        tags: categories,
        featured: false,
      };
    })
    .filter((a) => a.slug);
}

/**
 * Load blog articles from Squarespace RSS. Uses simple in-memory cache.
 * @param {{ force?: boolean }} opts
 */
export async function loadRssArticles(opts = {}) {
  const { force = false } = opts;
  if (!force && cachedArticles) return cachedArticles;
  if (!force && inFlightPromise) return inFlightPromise;

  async function fetchFirstWorkingXml() {
    let lastErr = null;
    for (const url of RSS_FALLBACK_URLS) {
      try {
        const res = await fetch(url, { method: "GET" });
        if (!res.ok) throw new Error(`RSS fetch failed: ${res.status}`);
        const txt = await res.text();
        // Basic sanity check: must contain <rss or <feed.
        if (!txt || (!txt.includes("<rss") && !txt.includes("<feed"))) {
          throw new Error("RSS response did not look like XML feed");
        }
        return txt;
      } catch (e) {
        lastErr = e;
      }
    }
    throw lastErr || new Error("RSS fetch failed");
  }

  inFlightPromise = fetchFirstWorkingXml()
    .then((xmlText) => {
      const articles = parseRssXmlToArticles(xmlText);
      cachedArticles = articles;
      cachedAtMs = Date.now();
      return articles;
    })
    .finally(() => {
      inFlightPromise = null;
    });

  return inFlightPromise;
}

export function getRssCacheInfo() {
  return {
    hasCache: !!cachedArticles,
    cachedCount: cachedArticles?.length ?? 0,
    cachedAtMs,
  };
}

